import { IonContent, IonHeader, IonPage, IonRow, IonCol, IonGrid, onIonViewWillEnter, onIonViewWillLeave } from '@ionic/vue';
import { computed, defineComponent, getCurrentInstance, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { Auth } from '@/services/AuthService';
import { AuthActions } from 'ionic-appauth';
import { settings } from 'ionicons/icons';
import AppToolbar from '@/components/app-structure/AppToolbar.vue';
import ProductCard from '@/components/cards/ProductCard.vue';
import AppFooter from "@/components/app-structure/AppFooter.vue";
import PushNotificationInit from "@/components/PushNotificationInit.vue";
import { AppLauncher } from '@capacitor/app-launcher';
import { Device } from '@capacitor/device';
import { useStore } from "vuex";
import TheLoader from "@/components/TheLoader.vue";
import { useOrientation } from "@/composables/useOrientation";
import * as Sentry from "@sentry/capacitor";
import * as SentryVue from "@sentry/vue";
import { Exception } from "sass";
import { jwtDecode } from 'jwt-decode';
export default defineComponent({
    name: 'HomePage',
    computed: {
        Exception() {
            return Exception;
        }
    },
    setup() {
        const router = useRouter();
        const openCampus = () => router.push({ name: 'CampusApp' });
        const openClickAndLearn = async () => {
            const { platform } = await Device.getInfo();
            if (platform === 'android') {
                const { value: canOpenUrl } = await AppLauncher.canOpenUrl({ url: 'de.degener.clicklearn.forms' });
                if (canOpenUrl) {
                    await AppLauncher.openUrl({ url: 'de.degener.clicklearn.forms' });
                    return;
                }
            }
            const link = document.getElementById('clickAndLearnLink');
            link?.click();
            return;
        };
        const openELearning = () => router.push({ name: 'ELearningAppPage' });
        const store = useStore();
        const loading = ref(false);
        const school = computed(() => store.state.school);
        const showSchoolCard = computed(() => {
            return (school.value.name + school.value.street + school.value.zipCode + school.value.city).length > 0;
        });
        const isValidBase64 = (str) => /^[A-Za-z0-9+/]+={0,2}$/.test(str);
        const formattedSchoolLogo = computed(() => isValidBase64(school.value.logo) ? `data:image/png;base64,${school.value.logo}` : school.value.logo);
        const { orientation } = useOrientation();
        const inLandscapeOrientation = computed(() => orientation.value.includes('landscape'));
        const checkIfSentryIsAllowedToLog = () => {
            const tokenResponse = localStorage.getItem('token_response');
            if (!tokenResponse)
                return false;
            let token = '';
            try {
                token = JSON.parse(tokenResponse).access_token || '';
            }
            catch (err) {
                console.error('[HomePage] ❌ Failed to parse token_response JSON:', err);
                return false;
            }
            const decodedJWTToken = jwtDecode(token);
            return decodedJWTToken.SentryTracing === 'true';
        };
        const closeSentry = () => {
            Sentry.setUser(null);
            Sentry.getCurrentScope().getClient()?.close();
        };
        const initSentry = () => {
            if (Sentry.getCurrentScope().getClient())
                return false;
            const isSentryAllowedToLog = checkIfSentryIsAllowedToLog();
            const integrations = [];
            if (isSentryAllowedToLog) {
                integrations.push(SentryVue.captureConsoleIntegration({ levels: ['error'] }));
            }
            Sentry.init({
                app: getCurrentInstance()?.appContext.app,
                dsn: process.env.VUE_APP_SENTRY_DNS,
                environment: process.env.NODE_ENV === 'production' ? 'production' : 'development',
                integrations,
                replaysSessionSampleRate: 0,
                replaysOnErrorSampleRate: 0,
                debug: process.env.VUE_APP_SENTRY_DEBUG === 'true',
                beforeSend: (event) => isSentryAllowedToLog ? event : null,
                beforeSendTransaction: (event) => isSentryAllowedToLog ? event : null,
                beforeBreadcrumb: (event) => isSentryAllowedToLog ? event : null
            });
        };
        const user = computed(() => store.state.user);
        onIonViewWillEnter(async () => {
            initSentry();
            loading.value = true;
            try {
                if (!user.value.loaded) {
                    await Auth.Instance.loadUserInfo();
                }
                if (!store.state.school.loaded) {
                    await store.dispatch('loadSchoolInfo');
                }
            }
            catch (error) {
                console.error(error);
            }
            finally {
                loading.value = false;
            }
        });
        onIonViewWillLeave(() => closeSentry());
        const subs = [];
        onMounted(async () => {
            subs.push(Auth.Instance.events$.subscribe((action) => {
                if (action.action === AuthActions.SignOutSuccess) {
                    router.push('/landing');
                }
            }));
        });
        return { settings, openCampus, openClickAndLearn, openELearning, showSchoolCard, loading, school, formattedSchoolLogo, inLandscapeOrientation };
    },
    components: {
        TheLoader,
        PushNotificationInit,
        AppFooter,
        AppToolbar,
        ProductCard,
        IonContent,
        IonHeader,
        IonPage,
        IonRow,
        IonCol,
        IonGrid
    }
});
